import { Button, IconButton, Menu, Tooltip } from "@itk/mui-components";
import { Accordion, AccordionDetails, AccordionSummary, Checkbox, Stack, Typography, useTheme, Zoom } from "@mui/material";
import { ChipList, DekoxIcon, TruncatedText } from "src/components";
import { getClickableProps } from "src/utils/keyboard-utils";
import { genericTranslationsK } from "src/translation-hooks/generic-translations";
import { MouseEvent, useState, useMemo, useEffect } from "react";
import { configuratorPageK } from "src/translation-hooks/configurator-page";
import { connect } from "react-redux";
import { configuratorActions } from "src/pages/configurator/configurator-store";
import { useConfiguratorTranslations } from "src/pages/configurator/configurator-translations";
import { ProductConfiguration } from "../product-configuration";
import { CopyMenu } from "../../../copy-menu";
import { useNavigate, useParams } from "react-router";
import { ROUTES_URL } from "src/utils/constants";
import { RootState } from "src/store";
import { addEditConfigurationDialogActions } from "../../../add-edit-configuration-dialog/add-edit-configuration-dialog-store";
import { Model_ConfigurationsGet, Shared_SystemSettings } from "src/backend/openapi/v1";
import { applicationActions } from "src/components/application-store";
import { getSystemProductConfigurationItemsHierarchy, HierarchyNode, collectExpandableItemIds, getConfigurationChipLabels, canCopyItem } from "src/pages/configurator/configurator-utils";
import { Model_LabelAndReferenceGet, Model_ConfigurationsGet_SystemProductConfigurationItem } from "src/backend/openapi/v1";
import { VIEW_SETTINGS_CONFIGURATION_LABELS_ITEM_ID } from "src/pages/configurator/components/view-settings/view-settings-store";
import { CONFIGURATION_NAME_FALLBACK_WIDTH, CONFIGURATION_NAME_WIDTH_VARIABLE } from "../../configurations-list-utils";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";

// Deliberately does NOT read selectedConfigurations. Subscribing every row to the whole selection
// array meant one checkbox click (or Select All) changed its identity and re-rendered all N rows,
// each scanning the array again — O(N²) on a 500-configuration project. The parent passes this row's
// own isSelected boolean instead, so only the toggled row re-renders.
const mapStateToProps = (state: RootState) => ({
  expandCollapseSignal: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.expandCollapseSignal,
  systemSettings: state.generalReducer.applicationReducer.systemSettings,
  viewSettings: state.pageReducer.combinedConfiguratorReducer.viewSettingsReducer.applied
});

const ownActions = {
  setAddEditConfigurationDialog: addEditConfigurationDialogActions.setAddEditConfigurationDialog,
  setFormData: addEditConfigurationDialogActions.setFormData,
  toggleSelectedConfiguration: configuratorActions.toggleSelectedConfiguration,
  deleteConfigurationsRequest: configuratorActions.deleteConfigurationsRequest,
  copyConfigurationsRequest: configuratorActions.copyConfigurationsRequest,
  getConfigurationsRequest: configuratorActions.getConfigurationsRequest,
  getLabelsRequest: configuratorActions.getLabelsRequest,
  setWarningAction: applicationActions.setWarningAction
};

type Props = typeof ownActions &
  ReturnType<typeof mapStateToProps> & {
    configuration: Model_ConfigurationsGet["configurations"][number];
    // Passed from ConfigurationsListBase, built once and shared across all accordions.
    // Avoids each Configuration building the full index independently (N × full scan).
    labelIndex: Map<string, Model_LabelAndReferenceGet[]>;
    // Resolved by the parent against a Set — see mapStateToProps above for why it isn't read here.
    isSelected: boolean;
  };

// Renders one tree node as a real TreeItem (chevron + connector-line nesting handled by
// SimpleTreeView itself — see ConfigurationBase below), recursing into its children. Only the
// top node (depth 0) can have multiple children — a child node can have at most one, so its
// add icon becomes disabled (not hidden) once that single child exists.
const ProductConfigurationNode = (props: {
  node: HierarchyNode<Model_ConfigurationsGet_SystemProductConfigurationItem>;
  depth: number;
  configuration: Model_ConfigurationsGet["configurations"][number];
  labelIndex: Map<string, Model_LabelAndReferenceGet[]>;
  setFormData: typeof addEditConfigurationDialogActions.setFormData;
  setAddEditConfigurationDialog: typeof addEditConfigurationDialogActions.setAddEditConfigurationDialog;
}) => {
  const { node, depth, configuration, labelIndex, setFormData, setAddEditConfigurationDialog } = props;

  const canAddChild =
    Boolean(configuration.systemProductConfigurationId && node.systemProductConfigurationItemId && node.systemPackageId && node.nextProductTypeId) && (depth === 0 || node.children.length === 0);

  const canCopy = canCopyItem(node);

  const handleAddChild = () => {
    setFormData({
      productsFilter: {
        parentProjectLineItemId: node.systemProductConfigurationItemId,
        systemPackageId: node.systemPackageId,
        productTypes: [node.nextProductTypeId ?? ""]
      },
      systemProductConfigurationId: configuration.systemProductConfigurationId
    });
    setAddEditConfigurationDialog({ step: "SELECT PRODUCT" });
  };

  return (
    <TreeItem
      itemId={node.systemProductConfigurationItemId ?? ""}
      label={
        <ProductConfiguration
          missingInformation={configuration.missingInformation || []}
          invalidInformation={configuration.invalidInformation || []}
          configurationId={configuration.systemProductConfigurationId || ""}
          projectLineItem={node}
          labelIndex={labelIndex}
          onAddChild={handleAddChild}
          canAddChild={canAddChild}
          canCopy={canCopy}
        />
      }
    >
      {node.children.length > 0 &&
        node.children.map(child => (
          <ProductConfigurationNode
            key={child.systemProductConfigurationItemId}
            node={child}
            depth={depth + 1}
            configuration={configuration}
            labelIndex={labelIndex}
            setFormData={setFormData}
            setAddEditConfigurationDialog={setAddEditConfigurationDialog}
          />
        ))}
    </TreeItem>
  );
};

const ConfigurationBase = (props: Props) => {
  const {
    configuration,
    isSelected,
    toggleSelectedConfiguration,
    setAddEditConfigurationDialog,
    setFormData,
    setWarningAction,
    deleteConfigurationsRequest,
    getConfigurationsRequest,
    copyConfigurationsRequest,
    getLabelsRequest,
    labelIndex,
    expandCollapseSignal,
    systemSettings,
    viewSettings
  } = props;

  const theme = useTheme();
  const navigate = useNavigate();
  // Resolved once for the whole page instead of per row — see configurator-translations.tsx
  const { configuratorPageT, genericTranslationsT } = useConfiguratorTranslations();

  const { projectId = "" } = useParams<{ projectId: string }>();

  // Seeded from the last Expand/Collapse All signal rather than a fixed default: rows mount as they
  // scroll into view, so a row reached after the user hit Expand All would otherwise come up collapsed.
  const [isExpanded, setIsExpanded] = useState(expandCollapseSignal?.action === "expand");
  const [copyBtnAnchor, setCopyBtnAnchor] = useState<HTMLButtonElement | null>(null);

  const copyConfigurationsLimit = parseInt(systemSettings[Shared_SystemSettings.CopyConfigurationsLimit] ?? "", 10);
  const hasValidCopyLimit = Number.isInteger(copyConfigurationsLimit) && copyConfigurationsLimit >= 1;

  // Memoize the hierarchy array so JSX map() and .last() don't re-run on every render.
  // systemProductConfigurationItems replaces projectLineItems on the list-view model
  const systemProductConfigurationItemHierarchy = useMemo(
    () => getSystemProductConfigurationItemsHierarchy(configuration.systemProductConfigurationItems || []),
    [configuration.systemProductConfigurationItems]
  );

  // Every node with children, so the tree renders fully expanded on first load — same as configure-product's tree.
  // Also doubles as the "expand all" target list below, since it's already every expandable node id.
  const defaultExpandedItemIds = useMemo(() => collectExpandableItemIds(systemProductConfigurationItemHierarchy), [systemProductConfigurationItemHierarchy]);

  // Controlled (not defaultExpandedItems) so the page-level Expand All/Collapse All signal can drive it,
  // and seeded from that signal for the same late-mount reason as isExpanded above
  const [expandedItemIds, setExpandedItemIds] = useState<string[]>(expandCollapseSignal?.action === "collapse" ? [] : defaultExpandedItemIds);

  // Page-level Expand All/Collapse All: opens/closes this card's own accordion and its nested item tree together
  useEffect(() => {
    if (!expandCollapseSignal) return;
    const expand = expandCollapseSignal.action === "expand";
    setIsExpanded(expand);
    setExpandedItemIds(expand ? defaultExpandedItemIds : []);
  }, [expandCollapseSignal?.requestId]);

  // Memoize the mapped string array — same reason as in ProductConfiguration: ChipList
  // uses referential equality on its labels prop and triggers ResizeObserver on change.
  // systemProductConfigurationId replaces configurationId on the list-view model
  const chipLabels = useMemo(() => getConfigurationChipLabels(configuration.systemProductConfigurationId ?? undefined, labelIndex), [labelIndex, configuration.systemProductConfigurationId]);

  // Configuration-level invalid indicator — true if the specific-value check flagged anything
  // (unchanged) OR any item in this configuration has its own IsValid flag set to false.
  const isConfigurationInvalid = useMemo(
    () => (configuration.invalidInformation && configuration.invalidInformation.length > 0) || (configuration.systemProductConfigurationItems ?? []).some(item => item.isValid === false),
    [configuration.invalidInformation, configuration.systemProductConfigurationItems]
  );

  // Forces this card's accordion open and its nested item tree fully expanded — same target
  // state as the page-level "expand all" (there's no per-node selection concept on this tree).
  const expandFully = () => {
    setIsExpanded(true);
    setExpandedItemIds(defaultExpandedItemIds);
  };

  const handleEdit = (e: MouseEvent) => {
    e.stopPropagation();
    // systemProductConfigurationId replaces configurationId for building the route
    navigate(ROUTES_URL.CONFIGURE_PRODUCT.build(projectId, configuration.systemProductConfigurationId?.toString() || ""));
  };

  const handleCopy = (blankValues: boolean, count: number) => {
    setCopyBtnAnchor(null);
    if (!configuration.systemProductConfigurationId) return;

    // Copies the configuration header directly (cascades to its items) instead of
    // approximating a copy via the root line item
    copyConfigurationsRequest(blankValues, { systemProductConfigurationIds: [configuration.systemProductConfigurationId], count }, isSuccess => {
      if (isSuccess) {
        getConfigurationsRequest(projectId);
        getLabelsRequest(projectId);
      }
    });
  };

  const handleDelete = (e: MouseEvent) => {
    e.stopPropagation();
    setWarningAction({
      confirm: () => {
        if (!configuration.systemProductConfigurationId) return;
        // systemProductConfigurationId replaces configurationId for delete operations
        deleteConfigurationsRequest({ configurationIds: [configuration.systemProductConfigurationId] }, isSuccess => {
          if (isSuccess) {
            getConfigurationsRequest(projectId);
          }
        });
        setWarningAction(undefined);
      },
      dialogProps: {
        message: configuratorPageT.areYouSureYouWantToDeleteThisProductConfiguration,
        confirmButtonTranslation: {
          t: genericTranslationsT,
          k: genericTranslationsK,
          field: "delete"
        }
      },
      variant: "confirmCancel"
    });
  };

  const open = Boolean(copyBtnAnchor);
  const id = open ? "copy-btn-popover" : undefined;

  return (
    <>
      <Accordion
        id={configuration.systemProductConfigurationId?.toString()}
        expanded={isExpanded}
        onChange={(_, expanded) => setIsExpanded(expanded)}
        // Unmount product cards when collapsed — avoids mounting N×M ChipList instances
        // (each with a ResizeObserver) just because the tab is visible.
        slotProps={{ transition: { unmountOnExit: true } }}
        sx={{
          boxShadow: "none",
          border: `1px solid ${theme.palette.customColors.grey}`,
          borderRadius: 1,
          overflow: "hidden",
          "&.Mui-expanded": {
            margin: 0,
            ".MuiSvgIcon-root[data-testid='KeyboardArrowDownIcon']": {
              transform: "rotate(180deg)"
            }
          },
          ":last-of-type": {
            borderRadius: 1
          }
        }}
      >
        <AccordionSummary
          sx={{
            overflow: "hidden",
            minHeight: 0,
            ".MuiAccordionSummary-content": {
              overflow: "hidden",
              my: 0
            }
          }}
        >
          <Stack width="100%" direction="row" justifyContent="space-between" alignItems="center" overflow="hidden">
            <Stack flex={1} direction="row" alignItems="center" overflow="hidden">
              <Checkbox
                size="small"
                checked={isSelected}
                onClick={e => e.stopPropagation()}
                onChange={e => {
                  e.stopPropagation();
                  // systemProductConfigurationId replaces configurationId for selection tracking
                  toggleSelectedConfiguration(configuration.systemProductConfigurationId || "");
                }}
              />
              <DekoxIcon icon="arrow-down" />
              {/* One width shared by every row (published by ConfigurationsList) so the product count and chips align across rows regardless of name length */}
              <Stack flex="none" minWidth={0} width={`var(${CONFIGURATION_NAME_WIDTH_VARIABLE}, ${CONFIGURATION_NAME_FALLBACK_WIDTH}px)`}>
                <TruncatedText variant="body2" fontWeight="500" ml={1} text={configuration.systemProductConfigurationName ?? ""} />
              </Stack>
              {/* fixed width so the chips that follow start at the same x-position on every row */}
              <Stack flex="none" width={100}>
                <Typography variant="caption" ml={1}>
                  {configuration.systemProductConfigurationItems?.length} {configuratorPageT.product}
                </Typography>
              </Stack>
              {configuration.systemProductConfigurationId && viewSettings.selectedItemIds.includes(VIEW_SETTINGS_CONFIGURATION_LABELS_ITEM_ID) && <ChipList labels={chipLabels} />}
            </Stack>

            <Stack direction="row" alignItems="center">
              {!isExpanded && (
                // Fixed to exactly two icon slots so the buttons below always land at the same
                // x-position; only the active icon(s) are mounted, packed against the right edge.
                <Stack direction="row" alignItems="center" justifyContent="flex-end" width={`calc(2 * (1.25rem + ${theme.spacing(2)}))`}>
                  {configuration.missingInformation && configuration.missingInformation.length > 0 && (
                    <Tooltip title={configuratorPageT.productIsMissingInformation} arrow placement="bottom" slots={{ transition: Zoom }}>
                      <DekoxIcon
                        icon="info"
                        fontSize="small"
                        color="warning"
                        onClick={(e: MouseEvent) => {
                          e.stopPropagation();
                          expandFully();
                        }}
                        {...getClickableProps(expandFully)}
                        sx={{ mx: 1, cursor: "pointer" }}
                      />
                    </Tooltip>
                  )}
                  {isConfigurationInvalid && (
                    <Tooltip title={configuratorPageT.productHasInvalidInformation} arrow placement="bottom" slots={{ transition: Zoom }}>
                      <DekoxIcon
                        icon="errorOutline"
                        fontSize="small"
                        color="error"
                        onClick={(e: MouseEvent) => {
                          e.stopPropagation();
                          expandFully();
                        }}
                        {...getClickableProps(expandFully)}
                        sx={{ mx: 1, cursor: "pointer" }}
                      />
                    </Tooltip>
                  )}
                </Stack>
              )}
              <IconButton
                k={genericTranslationsK.copy}
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setCopyBtnAnchor(e.currentTarget);
                }}
                sx={{ color: theme.palette.customColors.darkGrey }}
              >
                <DekoxIcon icon="copy" fontSize="small" />
              </IconButton>

              <IconButton k={genericTranslationsK.edit} size="small" onClick={handleEdit} sx={{ color: theme.palette.customColors.darkGrey }}>
                <DekoxIcon icon="edit" fontSize="small" />
              </IconButton>

              <IconButton k={genericTranslationsK.delete} size="small" onClick={handleDelete} sx={{ color: theme.palette.customColors.darkGrey }}>
                <DekoxIcon icon="delete" fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {systemProductConfigurationItemHierarchy.length === 0 && (
            <Button
              t={configuratorPageT}
              k={configuratorPageK}
              field="addProduct"
              variant="contained"
              startIcon={<DekoxIcon icon="add" />}
              sx={{ ml: 2 }}
              onClick={() => {
                setFormData({
                  systemProductConfigurationId: configuration.systemProductConfigurationId
                });
                setAddEditConfigurationDialog({ step: "SELECT SYSTEM" });
              }}
            />
          )}
          {systemProductConfigurationItemHierarchy.length > 0 && (
            <SimpleTreeView
              expansionTrigger="iconContainer"
              expandedItems={expandedItemIds}
              onExpandedItemsChange={(_, itemIds) => setExpandedItemIds(itemIds)}
              disableSelection
              sx={{
                "& .MuiTreeItem-groupTransition": {
                  position: "relative",
                  pl: 2,
                  "&::before": {
                    content: "''",
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: `calc(${theme.spacing(1)} + var(--TreeView-itemChildrenIndentation) * var(--TreeView-itemDepth) + 8px)`,
                    borderLeft: `2px solid ${theme.palette.customColors.grey}`,
                    pointerEvents: "none"
                  }
                },
                // "& >" restricts this to a ROOT-level item only (direct child of the tree, not a
                // nested item inside any .MuiTreeItem-groupTransition) — combined with :only-child and
                // an empty icon container, this only matches when the whole tree is a single root node
                // with no children and no siblings. Multi-item trees (and any node with siblings, at
                // any depth) keep their normal reserved icon column so rows stay aligned.
                "& > .MuiTreeItem-root:only-child > .MuiTreeItem-content > .MuiTreeItem-iconContainer:empty": {
                  display: "none"
                },
                // Same scoping for the content row's own base left padding (the constant term reserved
                // for a chevron) — keep the depth-indentation term (always 0 at root anyway) as-is
                "& > .MuiTreeItem-root:only-child > .MuiTreeItem-content:has(> .MuiTreeItem-iconContainer:empty)": {
                  paddingLeft: "calc(var(--TreeView-itemChildrenIndentation, 0px) * var(--TreeView-itemDepth, 0))"
                },
                "& .MuiTreeItem-content:hover": {
                  backgroundColor: "transparent"
                },
                "& .MuiTreeItem-content[data-focused]": {
                  backgroundColor: "transparent"
                },
                "& .MuiTreeItem-label": {
                  overflow: "visible"
                }
              }}
            >
              {systemProductConfigurationItemHierarchy.map(rootNode => (
                <ProductConfigurationNode
                  key={rootNode.systemProductConfigurationItemId}
                  node={rootNode}
                  depth={0}
                  configuration={configuration}
                  labelIndex={labelIndex}
                  setFormData={setFormData}
                  setAddEditConfigurationDialog={setAddEditConfigurationDialog}
                />
              ))}
            </SimpleTreeView>
          )}
        </AccordionDetails>
      </Accordion>
      <Menu id={id} anchorEl={copyBtnAnchor} open={open} onClose={() => setCopyBtnAnchor(null)}>
        <CopyMenu onCopy={handleCopy} maxCount={hasValidCopyLimit ? copyConfigurationsLimit : undefined} />
      </Menu>
    </>
  );
};

export const Configuration = connect(mapStateToProps, ownActions)(ConfigurationBase);
